/* AppraisalComponents/FasteningSectionAdornments.jsx
 * Phase C · C4b Fastening extraction (2026-02c · proof-pattern).
 *
 * Presentational section-header adornments for the Fastening work-type.
 * Extracted from AppraisalComponentsTab.js:3900-3913 (total-screws badge)
 * + 4211-4235 (auto-cascade hint bar). Zero React state · zero side
 * effects · pure props-in, JSX-out.
 *
 * Kept as two named exports rather than one combined component so
 * the parent orchestrator can slot each in its natural place inside
 * the section-header + section-body flow (they live on different rows).
 */
import React from 'react';

// Sum of screws to order across all Fastening lines. Renders a rose
// pill badge in the section header when the total is positive.
//   totalScrews = Σ ceil(units × screw_rate) per row · default rate = 7
export function FasteningTotalScrewsBadge({ lines }) {
  if (!lines || lines.length === 0) return null;
  const totalScrews = lines.reduce((acc, l) => {
    const m2 = Number(l.length) || 0;
    const rate = Number(l.screw_rate) || 7;
    return acc + Math.ceil(m2 * rate);
  }, 0);
  if (totalScrews <= 0) return null;
  return (
    <span
      data-testid="fastening-section-total-screws"
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5 font-mono-num"
    >
      {totalScrews.toLocaleString()} screws
    </span>
  );
}

// Auto-cascade hint bar surfaces the current quotation-type + profile
// context to the operator, plus a "See all N" section-level bypass.
// Only renders when the rate catalogue has > 1 row (nothing to cascade
// when there's only one option).
export function FasteningCascadeBar({
  wtRates,
  quoteType,
  roofProfile,
  sectionShowAllFastening,
  onToggleSectionShowAll,
}) {
  if (!wtRates || wtRates.length <= 1) return null;
  return (
    <div
      data-testid="fastening-cascade-bar"
      className="rounded-md border border-slate-200 bg-white px-3 py-2 flex items-center gap-2 flex-wrap"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
        Auto-cascade
      </span>
      <span className="text-[11px] text-slate-600">
        {quoteType
          ? (
            <>
              Picking fastenings for <strong className="text-slate-800">{quoteType}</strong>
              {roofProfile ? <> · profile <strong className="text-slate-800">{roofProfile}</strong></> : null}
            </>
          )
          : <span className="italic text-slate-400">Set a Quotation Type to auto-pick the right fastening size.</span>}
      </span>
      <button
        type="button"
        data-testid="section-show-all-fastening"
        onClick={onToggleSectionShowAll}
        title={sectionShowAllFastening
          ? `Cascade bypassed for every Fastening row — showing all ${wtRates.length} rates. Click to re-engage cascade.`
          : `Bypass cascade for every Fastening row — show all ${wtRates.length} rate rows in the catalogue.`}
        className={`ml-auto inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border transition-all ${
          sectionShowAllFastening
            ? 'bg-amber-100 text-amber-800 border-amber-300 ring-1 ring-amber-200'
            : 'bg-white text-slate-600 border-slate-300 hover:border-amber-300 hover:text-amber-700'
        }`}
      >
        {sectionShowAllFastening
          ? `▴ Showing all ${wtRates.length}`
          : `▾ See all ${wtRates.length}`}
      </button>
    </div>
  );
}
